// Random events. On each SeasonChange, roll a die — chance an event fires.
// Drought / Festival are duration-based and modify yield/rate multipliers
// while active; Bandits is instant (steals a slice of wealth).
//
// Event state persists per-kingdom (active().currentEvent). Expiry is
// wall-clock so an event running during a kingdom switch keeps ticking
// against real time.

import { Events, on, emit } from '../ui/eventBus.js'
import { EventConfig, EventTypes } from '../config/balance.js'
import { active, persist } from './state.js'

export function init() {
  on(Events.SeasonChange, ({ previous }) => {
    // Only roll on actual transitions, not on init re-broadcasts.
    if (previous) maybeRoll()
  })
  on(Events.KingdomChange, () => {
    // Re-emit current event for new active kingdom so the banner refreshes.
    // The persisted event only stores { type, expiresAt } — re-derive the
    // display fields (icon/label/desc) from config so the banner doesn't
    // render "undefined undefined undefined" on a re-broadcast.
    const ev = getActiveEvent()
    if (ev) {
      const cfg = findCfg(ev.type)
      emit(Events.EventTriggered, {
        type: ev.type,
        label: cfg?.label,
        icon: cfg?.icon,
        desc: cfg?.desc,
        action: cfg?.action,
        expiresAt: ev.expiresAt ?? 0,
        restored: true,
      })
    } else emit(Events.EventCleared)
  })
}

export function getActiveEvent() {
  const k = active()
  const ev = k.currentEvent
  if (!ev) return null
  if (ev.expiresAt && ev.expiresAt < Date.now()) {
    k.currentEvent = null
    persist()
    return null
  }
  return ev
}

export function getYieldMultiplier() {
  const ev = getActiveEvent()
  if (!ev) return 1
  return findCfg(ev.type)?.yieldMult ?? 1
}

export function getRateBonus() {
  const ev = getActiveEvent()
  if (!ev) return 0
  // Convert rate multiplier (e.g. 1.5) into a delta added to (1 + …) in
  // goonManager — matches the same pattern as building + feed bonuses.
  const mult = findCfg(ev.type)?.rateMult ?? 1
  return mult - 1
}

function findCfg(type) {
  return EventConfig.table.find((e) => e.type === type)
}

function maybeRoll() {
  if (Math.random() > EventConfig.rollChance) return
  const cfg = pickWeighted(EventConfig.table)
  fireEvent(cfg)
}

function pickWeighted(table) {
  const total = table.reduce((s, e) => s + e.weight, 0)
  let r = Math.random() * total
  for (const e of table) {
    r -= e.weight
    if (r <= 0) return e
  }
  return table[table.length - 1]
}

function fireEvent(cfg) {
  const k = active()
  let wealthBefore = k.wealth
  let wealthDelta = 0
  if (cfg.instantWealthMult !== 1) {
    const before = k.wealth
    k.wealth = Math.floor(before * cfg.instantWealthMult)
    wealthBefore = before
    wealthDelta = k.wealth - before
    emit(Events.WealthChange, { wealth: k.wealth, delta: wealthDelta })
  }
  if (cfg.durationMs > 0) {
    k.currentEvent = { type: cfg.type, expiresAt: Date.now() + cfg.durationMs }
  }
  persist()
  emit(Events.EventTriggered, {
    type: cfg.type,
    label: cfg.label,
    icon: cfg.icon,
    desc: cfg.desc,
    action: cfg.action, // what the player should do (or that nothing is needed) — never leave them guessing
    expiresAt: k.currentEvent?.expiresAt ?? 0,
    durationMs: cfg.durationMs,
    wealthDelta,   // what it really did to the treasury (0 if nothing) — the trip scribe quotes this
    wealthBefore,
  })
}

export { EventTypes }
